import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Clock, Plus, Edit, Trash2, Check, X, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";

type ShiftType = "day" | "evening" | "night" | "flexible" | "on-call";

interface ShiftFormData {
  name: string;
  type: ShiftType;
  startTime: string;
  endTime: string;
  enabled: boolean;
}

const SHIFT_TYPE_OPTIONS = [
  { value: "day", label: "Day Shift", requiresTime: true },
  { value: "evening", label: "Evening Shift", requiresTime: true },
  { value: "night", label: "Night Shift", requiresTime: true },
  { value: "flexible", label: "Flexible", requiresTime: false },
  { value: "on-call", label: "On-Call", requiresTime: false },
];

// Helper function to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
};

export function ShiftsTab() {
  const { toast } = useToast();
  const shifts = useQuery(api.shifts.list);
  const createShift = useMutation(api.shifts.create);
  const updateShift = useMutation(api.shifts.update);
  const deleteShift = useMutation(api.shifts.remove);
  const toggleEnabled = useMutation(api.shifts.toggleEnabled);

  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<Id<"shifts"> | null>(null);
  const [formData, setFormData] = useState<ShiftFormData>({
    name: "",
    type: "day",
    startTime: "09:00",
    endTime: "17:00",
    enabled: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setFormData({
      name: "",
      type: "day",
      startTime: "09:00",
      endTime: "17:00",
      enabled: true,
    });
    setErrors({});
    setIsCreating(false);
    setEditingId(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Shift name is required";
    }

    const shiftTypeConfig = SHIFT_TYPE_OPTIONS.find((opt) => opt.value === formData.type);

    if (shiftTypeConfig?.requiresTime) {
      if (!formData.startTime) {
        newErrors.startTime = "Start time is required";
      }
      if (!formData.endTime) {
        newErrors.endTime = "End time is required";
      }

      // Note: We allow shifts that cross midnight (e.g., 3:30 PM to 12:00 AM)
      // So we don't validate that end time must be after start time
      // This is intentional to support night shifts and shifts that span midnight
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const shiftTypeConfig = SHIFT_TYPE_OPTIONS.find((opt) => opt.value === formData.type);

      await createShift({
        name: formData.name,
        type: formData.type,
        startTime: shiftTypeConfig?.requiresTime ? formData.startTime : undefined,
        endTime: shiftTypeConfig?.requiresTime ? formData.endTime : undefined,
        enabled: formData.enabled,
      });

      toast({
        title: "Success",
        description: "Shift created successfully",
      });

      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create shift",
        variant: "destructive",
      });
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !validateForm()) return;

    try {
      const shiftTypeConfig = SHIFT_TYPE_OPTIONS.find((opt) => opt.value === formData.type);

      await updateShift({
        id: editingId,
        name: formData.name,
        startTime: shiftTypeConfig?.requiresTime ? formData.startTime : undefined,
        endTime: shiftTypeConfig?.requiresTime ? formData.endTime : undefined,
        enabled: formData.enabled,
      });

      toast({
        title: "Success",
        description: "Shift updated successfully",
      });

      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update shift",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: Id<"shifts">) => {
    if (!confirm("Are you sure you want to delete this shift?")) return;

    try {
      await deleteShift({ id });
      toast({
        title: "Success",
        description: "Shift deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete shift",
        variant: "destructive",
      });
    }
  };

  const handleToggleEnabled = async (id: Id<"shifts">) => {
    try {
      await toggleEnabled({ id });
      toast({
        title: "Success",
        description: "Shift status updated",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update shift status",
        variant: "destructive",
      });
    }
  };

  const startEdit = (shift: any) => {
    setEditingId(shift._id);
    setFormData({
      name: shift.name,
      type: shift.type,
      startTime: shift.startTime || "09:00",
      endTime: shift.endTime || "17:00",
      enabled: shift.enabled,
    });
    setIsCreating(true);
  };

  const shiftTypeConfig = SHIFT_TYPE_OPTIONS.find((opt) => opt.value === formData.type);
  const requiresTime = shiftTypeConfig?.requiresTime ?? false;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold">Shift Management</h2>
              <p className="text-sm text-gray-500">Configure employee shift types and schedules</p>
            </div>
          </div>
          {!isCreating && (
            <Button onClick={() => setIsCreating(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Add Shift
            </Button>
          )}
        </div>

        {/* Create/Edit Form */}
        {isCreating && (
          <div className="mt-4 border-t pt-4">
            <h3 className="mb-4 text-sm font-medium">
              {editingId ? "Edit Shift" : "Create New Shift"}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Shift Name */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  Shift Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Morning Shift"
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && (
                  <p className="flex items-center gap-1 text-sm text-red-500">
                    <AlertCircle className="h-3 w-3" />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Shift Type */}
              <div className="space-y-2">
                <Label htmlFor="type">
                  Shift Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: ShiftType) => setFormData({ ...formData, type: value })}
                  disabled={!!editingId} // Can't change type when editing
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SHIFT_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Time Range (only for day/evening/night shifts) */}
              {requiresTime && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="startTime">
                      Start Time <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="startTime"
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className={errors.startTime ? "border-red-500" : ""}
                    />
                    {errors.startTime && (
                      <p className="flex items-center gap-1 text-sm text-red-500">
                        <AlertCircle className="h-3 w-3" />
                        {errors.startTime}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="endTime">
                      End Time <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="endTime"
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className={errors.endTime ? "border-red-500" : ""}
                    />
                    {errors.endTime && (
                      <p className="flex items-center gap-1 text-sm text-red-500">
                        <AlertCircle className="h-3 w-3" />
                        {errors.endTime}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Enabled Toggle */}
            <div className="mt-4 flex items-center gap-2">
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
              <Label htmlFor="enabled" className="cursor-pointer">
                Enable this shift
              </Label>
            </div>

            {/* Form Actions */}
            <div className="mt-4 flex gap-2">
              <Button onClick={editingId ? handleUpdate : handleCreate} className="gap-2">
                <Check className="h-4 w-4" />
                {editingId ? "Update" : "Create"} Shift
              </Button>
              <Button onClick={resetForm} variant="outline" className="gap-2">
                <X className="h-4 w-4" /> Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Shifts List */}
      <Card className="p-6">
        <h3 className="mb-4 text-sm font-medium">Configured Shifts</h3>

        {!shifts || shifts.length === 0 ? (
          <EmptyState
            icon={Clock}
            title="No shifts configured yet"
            description="Create your first shift to get started."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Time Range</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shifts.map((shift: any) => {
                  const typeLabel = SHIFT_TYPE_OPTIONS.find(
                    (opt) => opt.value === shift.type
                  )?.label;

                  return (
                    <TableRow key={shift._id}>
                      <TableCell className="font-medium">{shift.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{typeLabel}</Badge>
                      </TableCell>
                      <TableCell>
                        {shift.startTime && shift.endTime
                          ? `${formatTime12Hour(shift.startTime)} - ${formatTime12Hour(shift.endTime)}`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={shift.enabled ? "default" : "secondary"}
                          className={cn(
                            shift.enabled
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : "bg-gray-100 text-gray-800"
                          )}
                        >
                          {shift.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleEnabled(shift._id)}
                          >
                            {shift.enabled ? "Disable" : "Enable"}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => startEdit(shift)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(shift._id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
